import { createRouter, createWebHistory } from 'vue-router'
import Diary from '../views/Diary.vue'
import ProfileHub from '../views/ProfileHub.vue'
import ProfileSettings from '../views/ProfileSettings.vue'
import ProfileBody from '../views/ProfileBody.vue'
import ProfileActivity from '../views/ProfileActivity.vue'
import ProfileAllergies from '../views/ProfileAllergies.vue'
import ProfileNutrition from '../views/ProfileNutrition.vue'
import ProfileGoals from '../views/ProfileGoals.vue'
import KnowledgeBase from '../views/KnowledgeBase.vue'
import Cooking from '../views/Cooking.vue'
import DishPage from '../views/DishPage.vue'
import ZoyaDialog from '../views/ZoyaDialog.vue'
import YandexCallback from '../views/YandexCallback.vue'
import VkCallback from '../views/VkCallback.vue'
import TelegramCallback from '../views/TelegramCallback.vue'
import ArticleView from '../views/ArticleView.vue'
import LegalTerms from '../views/LegalTerms.vue'
import LegalPrivacy from '../views/LegalPrivacy.vue'
import LegalConsent from '../views/LegalConsent.vue'
import HelpInstall from '../views/HelpInstall.vue'
import HelpSupport from '../views/HelpSupport.vue'
import SubscriptionPage from '../views/SubscriptionPage.vue'
import { useAuthStore } from '../store/auth.js'
import { track } from '../analytics.js'
import { lazyLoad } from '../utils/lazyLoad.js'

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior() { return { top: 0 } },
  routes: [
    { path: '/', redirect: '/diary' },
    { path: '/diary', component: Diary, meta: { title: 'Дневник' } },
    { path: '/profile', component: ProfileHub, meta: { title: 'Профиль' } },
    { path: '/profile/settings', component: ProfileSettings, meta: { title: 'Настройки Дневника' } },
    { path: '/profile/communication', redirect: '/zoya/settings' },
    { path: '/profile/notifications', component: lazyLoad(() => import('../views/NotificationSettings.vue')), meta: { title: 'Настройки уведомлений' } },
    { path: '/profile/account', component: lazyLoad(() => import('../views/ProfileAccount.vue')), meta: { title: 'Настройки аккаунта' } },
    { path: '/profile/subscription', component: lazyLoad(() => import('../views/SubscriptionManagement.vue')), meta: { title: 'Управление подпиской' } },
    // Вход по ссылке из письма: ?e=<почта base64url>&c=<код>&next=<куда>.
    // Для тех, кто входил через Apple: на сайте входа через Apple нет, а почта
    // у них скрытая, её не наберёшь. См. lib/limitEmail.js.
    { path: '/login', component: lazyLoad(() => import('../views/MagicLogin.vue')), meta: { title: 'Вход' } },
    { path: '/profile/invite', component: lazyLoad(() => import('../views/ProfileInvite.vue')), meta: { title: 'Пригласить друга' } },
    { path: '/profile/payment-history', component: lazyLoad(() => import('../views/PaymentHistory.vue')), meta: { title: 'История платежей' } },
    { path: '/profile/body', component: ProfileBody, meta: { title: 'Моё тело' } },
    { path: '/profile/activity', component: ProfileActivity, meta: { title: 'Физическая активность' } },
    { path: '/profile/allergies', component: ProfileAllergies, meta: { title: 'Продукты в рационе' } },
    { path: '/profile/nutrition', component: ProfileNutrition, meta: { title: 'Рекомендации по питанию' } },
    { path: '/profile/goals', component: ProfileGoals, meta: { title: 'Цели' } },
    { path: '/knowledge', component: KnowledgeBase, meta: { title: 'База знаний' } },
    { path: '/knowledge/articles/:slug', component: ArticleView, meta: { title: 'Статья' } },
    { path: '/cooking', component: Cooking, meta: { title: 'Кулинария' } },
    { path: '/cooking/dish/:id', component: DishPage, meta: { title: 'Блюдо' } },
    { path: '/zoya', component: ZoyaDialog, meta: { title: 'Зоя' } },
    { path: '/zoya/settings', component: ZoyaDialog, meta: { title: 'Настройки Зои' } },
    { path: '/onboarding', component: lazyLoad(() => import('../views/Onboarding.vue')), meta: { title: 'Знакомство', isOnboarding: true } },
    { path: '/subscription', component: SubscriptionPage, meta: { title: 'Подписка' } },
    { path: '/bug-report', component: lazyLoad(() => import('../views/BugReport.vue')), meta: { title: 'Сообщить об ошибке' } },
    { path: '/report/week', component: lazyLoad(() => import('../views/WeekReport.vue')), meta: { title: 'Отчет за неделю' } },
    { path: '/report/week/preview/:previewId', component: lazyLoad(() => import('../views/WeekReport.vue')), meta: { title: 'Предпросмотр отчета' } },
    { path: '/admin', component: lazyLoad(() => import('../views/Admin.vue')), meta: { title: 'Админка' } },
    { path: '/admin/encyclopedia', component: lazyLoad(() => import('../views/EncyclopediaEditor.vue')), meta: { title: 'Редактор Энциклопедии' } },
    { path: '/admin/articles', component: lazyLoad(() => import('../views/AdminArticles.vue')), meta: { title: 'Статьи' } },
    { path: '/admin/versions', component: lazyLoad(() => import('../views/AdminVersions.vue')), meta: { title: 'Версии приложения' } },
    { path: '/admin/errors', component: lazyLoad(() => import('../views/AdminErrors.vue')), meta: { title: 'Логи ошибок' } },
    { path: '/admin/reminders', component: lazyLoad(() => import('../views/AdminReminders.vue')), meta: { title: 'Напоминания' } },
    { path: '/admin/articles/:id', component: lazyLoad(() => import('../views/AdminArticleEdit.vue')), meta: { title: 'Редактор статьи' } },
    { path: '/admin/nutrient-audit', component: lazyLoad(() => import('../views/NutrientAudit.vue')), meta: { title: 'Аудит нутриентов' } },
    { path: '/admin/zh-home', component: lazyLoad(() => import('../views/AdminZhHome.vue')), meta: { title: 'Главная zozhnik.ru' } },
    { path: '/legal/ios/terms', component: LegalTerms, meta: { title: 'Документы Зои' } },
    { path: '/legal/ios/privacy', component: LegalPrivacy, meta: { title: 'Документы Зои' } },
    { path: '/legal/ios/consent', component: LegalConsent, meta: { title: 'Документы Зои' } },
    { path: '/legal/pwa/terms', component: LegalTerms, meta: { title: 'Документы Зои' } },
    { path: '/legal/pwa/privacy', component: LegalPrivacy, meta: { title: 'Документы Зои' } },
    { path: '/legal/pwa/consent', component: LegalConsent, meta: { title: 'Документы Зои' } },
    { path: '/legal/terms', component: LegalTerms, meta: { title: 'Пользовательское соглашение' } },
    { path: '/legal/privacy', component: LegalPrivacy, meta: { title: 'Политика персональных данных' } },
    { path: '/legal/consent', component: LegalConsent, meta: { title: 'Согласие на обработку ПДн' } },
    { path: '/install', redirect: '/help/install' },
    { path: '/help/install', component: HelpInstall, meta: { title: 'Установка приложения' } },
    { path: '/support', redirect: '/help/support' },
    { path: '/help/support', component: HelpSupport, meta: { title: 'Помощь и поддержка' } },
    { path: '/auth/yandex/callback', component: YandexCallback },
    { path: '/auth/vk/callback', component: VkCallback },
    { path: '/auth/telegram/callback', component: TelegramCallback },
  ],
})

router.beforeEach((to) => {
  const auth = useAuthStore()

  if (to.path.startsWith('/admin')) {
    if (!auth.user?.is_admin) return '/profile'
  }

  // Незавершённая анкета больше не закрывает приложение.
  //
  // Раньше здесь стояли два запрета: гостя без Phase 1 пускали только в
  // онбординг, а зарегистрированного с onboarding_done = false — только в
  // «Зою», профиль и подписку. Со стороны это выглядело поломкой: человек
  // жал «Поиск», «Кулинарию» и «+», а роутер молча возвращал его на ту же
  // страницу. Жалоба 3 сентября 2026 звучала как «работают только две
  // кнопки, остальные не кликабельны» — и это было ровно оно.
  //
  // Держать анкету обязательной было незачем: дневник умеет жить без неё,
  // подставляя запасные нормы (см. NORMS в Diary.vue), база знаний не
  // требует даже авторизации, а «Кулинария» просто покажет пустые вкладки.
  // Вместо запрета дневник зовёт заполнить анкету строкой над шкалами.

  // Единственное исключение — диалог с Зоей у гостя без второй части анкеты:
  // он опирается на цели, которых ещё нет. Ведём на нужный шаг, а не в никуда.
  if (auth.user?.is_guest && to.path === '/zoya' && !auth.user?.goals_data) {
    return auth.user?.onboarding_done ? '/onboarding?phase=2' : '/onboarding'
  }
  // То же для зарегистрированного, у которого Phase 2 отложена: вошёл с
  // первого экрана онбординга (deferToPhase2 в store/onboarding.js). Смотрим
  // именно на отложенную Phase 2 в onboarding_state, а не на пустые цели:
  // у сотен старых аккаунтов целей нет, и чат у них должен открываться как раньше.
  if (!auth.user?.is_guest && to.path === '/zoya' && !auth.user?.goals_data
      && auth.user?.onboarding_state?.phase === 2) {
    return '/onboarding?phase=2'
  }

  // Неавторизованным: перенаправляем на онбординг (Duolingo-style)
  // /login — вход по ссылке из письма: без сессии по определению, см. MagicLogin.vue
  const publicPaths = ['/legal/', '/auth/', '/help/', '/install', '/onboarding', '/login']
  const isPublic = publicPaths.some((p) => to.path.startsWith(p))

  if (!auth.token && !auth.isTelegramApp) {
    if (isPublic) {
      auth.closeAuthPopup()
    } else {
      // Новые пользователи без токена → на онбординг (вместо auth popup)
      return '/onboarding'
    }
  }
})

// Успешная навигация — сбрасываем флаг chunk-reload (готовы к следующему деплою)
router.afterEach(() => {
  sessionStorage.removeItem('chunk-reload')
})

// Store route patterns only; never URLs, query strings, tokens or record IDs.
router.afterEach((to) => {
  if (!to.path.startsWith('/admin')) {
    const pattern=to.matched?.[0]?.path || '/'
    if (!pattern.includes(':')) track('screen_viewed', {screen:pattern})
  }
})

export default router
